// client/src/components/plataforma/contas/ContasPage.js
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Building, Loader2, Edit, Users, LogIn, LayoutDashboard, MoreVertical } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';
import { useAccounts } from '../../../hooks/useAccounts';
import { useAuth } from '../../../context/auth/AuthProvider';
import { useAuthMutations } from '../../../hooks/useAuth';
import { isMatrizAdmin, isFranquiaAdmin } from '../../../utils/permissions';
import useAlert from '../../../hooks/useAlert'; // ✅ Importar hook de alertas

const ContaRow = ({ conta, isMatrizView, onOpenActions }) => {
    return (
        <tr key={conta._id} onClick={() => onOpenActions(conta)} style={{ cursor: 'pointer' }}>
            <td className="td-name">
                <div className="table-icon-wrapper icon-pf"><Building size={14} /></div>
                {conta.name}
            </td>
            <td>{conta.owner?.name || 'N/A'}</td>
            {isMatrizView && <td>{conta.franquia?.nome || 'Direta (Matriz)'}</td>}
            <td>
                <span className={`badge ${conta.status === 'trialing' ? 'badge-warning' : (conta.status === 'active' ? 'badge-success' : 'badge-danger')}`}>
                    {conta.status}
                </span>
            </td>
            <td className="td-actions">
                <button
                    onClick={(e) => { e.stopPropagation(); onOpenActions(conta); }}
                    title="Ações"
                    className="action-button-icon text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <MoreVertical size={18} />
                </button>
            </td>
        </tr>
    );
};

const ContasPage = () => {
    const { franquiaId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { data: accounts = [], isLoading: accountsLoading, error: accountsError } = useAccounts(franquiaId);
    const { setAlert } = useAlert(); // ✅ Inicializar hook de alertas
    const { impersonateUser } = useAuthMutations();
    const [selectedConta, setSelectedConta] = useState(null);

    // ✅ Exibir erro se houver
    React.useEffect(() => {
        if (accountsError) {
            setAlert(accountsError.message || 'Erro ao carregar contas', 'danger');
        }
    }, [accountsError, setAlert]);

    const isMatrizView = !franquiaId;
    const pageTitle = isMatrizView ? 'Gestão de Todas as Contas' : 'Gestão de Contas da Franquia';
    const newAccountLink = isMatrizView ? '/contas/novo' : `/franquias/${franquiaId}/contas/novo`;

    const handleImpersonateClick = (conta) => {
        if (!conta.owner?._id) {
            setAlert('Esta conta não possui um proprietário para personificar.', 'warning');
            return;
        }
        if (window.confirm(`Você tem certeza que deseja logar como ${conta.owner.name}, o proprietário da conta "${conta.name}"?`)) {
            impersonateUser.mutate({ userId: conta.owner._id, accountId: conta._id }, {
                onError: (error) => {
                    setAlert(error.message || 'Erro ao fazer login como proprietário', 'danger');
                }
            });
        }
        setSelectedConta(null);
    };

    const editAccountLink = (conta) => isMatrizView
        ? `/contas/${conta._id}/editar`
        : `/franquias/${franquiaId}/contas/${conta._id}/editar`;

    const actions = selectedConta ? [
        // role.name é rótulo de exibição do banco, nunca 'matriz_admin'/
        // 'franquia_admin' — a checagem antiga nunca era verdadeira, esse
        // botão nunca aparecia pra ninguém. Corrigido pra isMatrizAdmin/
        // isFranquiaAdmin, o mesmo utilitário já usado no resto do app.
        ...(isMatrizAdmin(user) || isFranquiaAdmin(user) ? [{
            key: 'login',
            label: `Logar como ${selectedConta.owner?.name}`,
            icon: <LogIn size={20} className="icon" />,
            onClick: () => handleImpersonateClick(selectedConta),
            loading: impersonateUser.isPending,
        }] : []),
        {
            key: 'editar',
            label: 'Editar Conta',
            icon: <Edit size={20} className="icon" />,
            onClick: () => { navigate(editAccountLink(selectedConta)); setSelectedConta(null); },
        },
        {
            key: 'dashboard',
            label: 'Gerenciar Conta',
            icon: <LayoutDashboard size={20} className="icon" />,
            onClick: () => { navigate(`/contas/${selectedConta._id}/dashboard`); setSelectedConta(null); },
        },
    ] : [];

    return (
        <div className="page-management-container">
            <PageHeader
                icon={isMatrizView ? <Users /> : <Building />}
                title={pageTitle}
                navBack={!isMatrizView ? { to: '/franquias', text: 'Voltar' } : null}
            />

            <div className="table-container mt-4">
                <div className="table-wrapper">
                    <table className="styled-table">
                        <thead>
                            <tr>
                                <th>Nome da Conta</th>
                                <th>Proprietário</th>
                                {isMatrizView && <th>Franquia</th>}
                                <th>Status</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accountsLoading ? (
                                <tr><td colSpan={isMatrizView ? 5 : 4} className="p-4 text-center text-gray-500"><Loader2 className="animate-spin inline-block mr-2" />Carregando contas...</td></tr>
                            ) : accounts.length > 0 ? (
                                accounts.map((conta) => (
                                    <ContaRow
                                        key={conta._id}
                                        conta={conta}
                                        isMatrizView={isMatrizView}
                                        onOpenActions={setSelectedConta}
                                    />
                                ))
                            ) : (
                                <tr><td colSpan={isMatrizView ? 5 : 4} className="p-8 text-center">Nenhuma conta cadastrada.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ActionsDrawer
                open={!!selectedConta}
                onClose={() => setSelectedConta(null)}
                title={selectedConta?.name || 'Conta'}
                subtitle={selectedConta?.owner?.name}
                actions={actions}
            />

            <FabButton onClick={() => navigate(newAccountLink)} title="Nova Conta" icon={<Building size={26} className="icon"/>} />
        </div>
    );
};

export default ContasPage;
